import ChevronLeft from "@mui/icons-material/ChevronLeft";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view";

import type { TreeViewItem } from "../utils";
import type { TreeItemProps } from "@mui/x-tree-view";
import type { FC, JSXElementConstructor, SyntheticEvent } from "react";

const SIDEBAR_WIDTH = 280;

interface ContentLibrarySidebarProps {
  loading: boolean;
  sidebarOpen: boolean;
  treeItems: TreeViewItem[];
  selectedTreeItemId: string | null;
  expandedItems: string[];
  onToggle: (open: boolean) => void;
  onItemClick: (itemId: string) => void;
  onExpandedItemsChange: (event: SyntheticEvent | null, itemIds: string[]) => void;
  onCreateSubFolder: () => void;
  itemSlot: JSXElementConstructor<TreeItemProps>;
}

export const ContentLibrarySidebar: FC<ContentLibrarySidebarProps> = ({
  loading,
  sidebarOpen,
  treeItems,
  selectedTreeItemId,
  expandedItems,
  onToggle,
  onItemClick,
  onExpandedItemsChange,
  onCreateSubFolder,
  itemSlot,
}) => (
  <Box
    sx={{
      width: sidebarOpen ? SIDEBAR_WIDTH : 60,
      flexShrink: 0,
      bgcolor: sidebarOpen ? "background.default" : "transparent",
      transition: "width 0.3s ease-in-out, background-color 0.3s ease-in-out",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    }}
  >
    {sidebarOpen ? (
      <Stack
        sx={{
          width: SIDEBAR_WIDTH,
          px: "16px",
          pt: "16px",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
        gap={2}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight={500} fontSize={16} color="text.primary">
            Folders
          </Typography>
          <ChevronLeft
            sx={{ cursor: "pointer" }}
            onClick={() => onToggle(false)}
            data-analytics-id="content-library-close-sidebar"
            aria-label="Close sidebar"
          />
        </Box>
        {loading && <FoldersTreeSkeleton />}
        {!loading && (
          <RichTreeView
            items={treeItems}
            onItemClick={(_, id) => onItemClick(id)}
            selectedItems={selectedTreeItemId}
            expandedItems={expandedItems}
            onExpandedItemsChange={onExpandedItemsChange}
            slots={{ item: itemSlot }}
            expansionTrigger="iconContainer"
          />
        )}
        {loading || (treeItems[0]?.children && treeItems[0]?.children.length > 0) ? null : (
          <Button
            onClick={onCreateSubFolder}
            data-analytics-id="content-library-sidebar-create-sub-folder"
          >
            Create sub-folder
          </Button>
        )}
      </Stack>
    ) : (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: "16px",
          pl: "16px",
        }}
      >
        <Box
          sx={{
            borderRadius: "4px",
            border: "1px solid",
            borderColor: "neutral.300",
            p: "8px",
            pb: "4px",
            color: "text.secondary",
          }}
        >
          <FolderOutlined
            sx={{ cursor: "pointer" }}
            onClick={() => onToggle(true)}
            data-analytics-id="content-library-open-sidebar"
            aria-label="Open sidebar"
            width={24}
            height={24}
          />
        </Box>
      </Box>
    )}
  </Box>
);

const FoldersTreeSkeleton: FC = () => {
  const rows = [
    { width: "70%", indent: 0 },
    { width: "55%", indent: 0 },
    { width: "60%", indent: 1 },
    { width: "48%", indent: 1 },
    { width: "65%", indent: 0 },
  ];

  return (
    <Stack gap={1} sx={{ pl: 3, pt: 1 }}>
      {rows.map((row, index) => (
        <Box
          key={`${row.width}-${index}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: row.indent * 2,
          }}
        >
          <Skeleton variant="circular" width={16} height={16} />
          <Skeleton variant="text" width={row.width} height={24} />
        </Box>
      ))}
    </Stack>
  );
};
